import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { UserContext } from '../../UserContext'; // ✅ Import User Context

const Communication = () => {
  const { user } = useContext(UserContext); // ✅ Get user from context
  const category = 'communication'; // ✅ Category for fetching videos
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`https://wholeness-forward.onrender.com/api/video/videos/${category}`);
        if (Array.isArray(response.data)) {
          setVideos(response.data);
        }
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // ✅ Handle Mark as Helpful for logged-in users only
  const handleMarkHelpful = async (videoId) => {
    if (!user || !user.id) {
      alert('You must be logged in to mark as helpful.');
      return;
    }

    try {
      const response = await axios.post(`https://wholeness-forward.onrender.com/api/video/videos/toggleHelpful`, {
        videoId,
        userId: user.id,
      });

      // ✅ Update UI with new helpful count
      setVideos((prevVideos) =>
        prevVideos.map((video) =>
          video._id === videoId
            ? { ...video, helpfulCount: response.data.helpfulCount }
            : video
        )
      );
    } catch (error) {
      console.error('Error marking video as helpful:', error);
    }
  };

  return (
    <div className="page-container communication-page">
      <h1 className="page-title">Effective Communication</h1>

      {/* Include the image */}
      <img
        src="/images/wf-effectivecommunication.webp"
        alt="Effective Communication"
        className="page-image"
      />

      <p className="page-intro">Welcome to the Effective Communication section of Wholeness Forward.</p>

      <p className="page-text">
        Effective communication is the foundation of all healthy relationships—whether personal or professional.
        It involves the clear exchange of ideas, emotions, and information in a way that is both understood and respected.
        By mastering communication skills, you can enhance your relationships, resolve conflicts, and express yourself with confidence.
      </p>

      <hr className="page-divider" />

      <h2 className="page-heading">Subtopics</h2>
      <ul className="content-list">
        <li><Link to="/relational/listening" className="content-link">Active Listening</Link></li>
        <li><Link to="/relational/speaking" className="content-link">Speaking with Clarity</Link></li>
      </ul>

      <hr className="page-divider" />

      <h3 className="subheading">Active Listening</h3>
      <p className="page-text">
        Active listening is the practice of fully concentrating on what is being said rather than just passively hearing the speaker’s words.
        It involves not only listening to the words but also understanding the meaning behind them.
        Active listening improves relationships, fosters empathy, and ensures that communication is clear and effective.
      </p>

      <hr className="page-divider" />

      <h3 className="subheading">Speaking with Clarity</h3>
      <p className="page-text">
        Clear and concise speaking is essential for effective communication. Whether you’re sharing an idea with a colleague or having a heart-to-heart conversation with a loved one, speaking clearly helps others understand your thoughts and intentions.
      </p>

      <hr className="page-divider" />

      <h3 className="subheading">Non-Verbal Communication</h3>
      <p className="page-text">
        Non-verbal communication includes body language, facial expressions, gestures, posture, and tone of voice.
        These cues often convey more than words alone, and they can strengthen or contradict what is being said.
      </p>

      <hr className="page-divider" />

      <h2 className="page-heading">Curated Video Collection</h2>
      {loading ? (
        <p>Loading videos...</p>
      ) : (
        <div className="video-container">
          {videos.length > 0 ? (
            videos.map((video) => (
              <div key={video._id} className="video-card">
                <h3 className="video-title">{video.title}</h3>
                <p className="video-details">
                  <strong>Source:</strong> {video.source} <br />
                  <strong>Length:</strong> {video.length} <br />
                  {video.description}
                </p>
                <iframe
                  className="video-frame"
                  width="560"
                  height="315"
                  src={video.url}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
                <div className="video-actions">
                  {/* ✅ Helpful Button */}
                  <button
                    className="button"
                    onClick={() => handleMarkHelpful(video._id)}
                    disabled={video.usersWhoClickedHelpful?.includes(user?.id)}
                  >
                    {video.usersWhoClickedHelpful?.includes(user?.id)
                      ? `Helpful (${video.helpfulCount})`
                      : `Mark as Helpful (${video.helpfulCount || 0})`}
                  </button>
                </div>
                <hr />
              </div>
            ))
          ) : (
            <p className="no-videos-message">No videos found for this category.</p>
          )}
        </div>
      )}

      <hr className="page-divider" />

      <h3 className="subheading">Join the Communication Community</h3>
      <p className="page-text">
        Share your experiences with communication, ask questions, and connect with others who are focused on improving their communication skills.
        Whether you're looking to be a better listener or speaker, this forum provides a space for learning and growth.
      </p>
      <a href="/forum/communication" className="btn">Join the Forum</a>
    </div>
  );
};

export default Communication;
